import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Loader2, Mail, Plus, RefreshCw, ShieldCheck, ShieldOff, Trash2, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_TONE = {
  admin: { text: '#c9a3f5', bg: 'rgba(200,163,245,0.10)', border: 'rgba(200,163,245,0.35)' },
  curator: { text: '#90E0EF', bg: 'rgba(144,224,239,0.10)', border: 'rgba(144,224,239,0.35)' },
};

const STATUS_TONE = {
  active: { text: '#7ed4a0', label: 'Active' },
  invited: { text: '#e4c07a', label: 'Invited' },
  disabled: { text: '#e28f8f', label: 'Disabled' },
};

function RoleBadge({ role }) {
  const t = ROLE_TONE[role] || ROLE_TONE.curator;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.2em]"
      style={{ color: t.text, background: t.bg, border: `1px solid ${t.border}` }}
    >
      {role}
    </span>
  );
}

function StatusPill({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.invited;
  return (
    <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: t.text }}>
      {t.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // { user, invite_url, expires_at }

  const fetchRows = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/users`)
      .then((r) => setRows(r.data || []))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Failed to load users.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRows(); }, []);

  const doPatch = async (id, body) => {
    try {
      await axios.patch(`${API}/admin/users/${id}`, body);
      toast.success('Saved.');
      fetchRows();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Update failed.');
    }
  };

  const doResend = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/users/${id}/resend-invite`);
      setLastInvite({ user: rows.find((r) => r.id === id), invite_url: data.invite_url, expires_at: data.expires_at });
      toast.success('Invite re-sent.');
      fetchRows();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to resend invite.');
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      toast.success('User deleted.');
      fetchRows();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Delete failed.');
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-white tracking-tight">Users</h1>
          <p className="mt-1 text-[11px] font-mono text-[#666]">Manage admins and curators. Invite new members with a shareable link.</p>
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          data-testid="users-invite-open"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#90E0EF] text-black text-[11px] font-mono uppercase tracking-[0.2em] hover:bg-white transition-colors"
          style={{ cursor: 'pointer' }}
        >
          <UserPlus className="w-4 h-4" /> Invite user
        </button>
      </div>

      {lastInvite && (
        <div className="mb-6 border border-[#90E0EF]/30 bg-[#0a0f11] p-4" data-testid="users-invite-copy-strip">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#90E0EF]">Latest invite link</div>
              <div className="mt-1 text-[11px] font-mono text-[#888] truncate">{lastInvite.user?.email}</div>
              <div className="mt-2 text-[11px] font-mono text-[#e8e8e8] break-all">{lastInvite.invite_url}</div>
              <div className="mt-1 text-[10px] font-mono text-[#666]">Expires {fmtDate(lastInvite.expires_at)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(lastInvite.invite_url); toast.success('Copied.'); }}
                className="inline-flex items-center gap-2 px-3 py-2 border border-[#333] text-[10px] font-mono uppercase tracking-[0.2em] text-[#c8c8c8] hover:border-[#90E0EF] hover:text-[#90E0EF] transition-colors"
                style={{ cursor: 'pointer' }}
                data-testid="users-invite-copy"
              >
                <Copy className="w-3.5 h-3.5" /> Copy link
              </button>
              <button
                type="button"
                onClick={() => setLastInvite(null)}
                className="p-2 text-[#666] hover:text-white"
                style={{ cursor: 'pointer' }}
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-[#1a1a1a] bg-[#070707]">
        <div className="grid grid-cols-[1fr_1fr_100px_100px_150px_200px] gap-4 px-4 py-3 border-b border-[#1a1a1a] text-[9px] font-mono uppercase tracking-[0.2em] text-[#555]">
          <div>Name</div>
          <div>Email</div>
          <div>Role</div>
          <div>Status</div>
          <div>Last login</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 text-[#90E0EF] mx-auto animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-[11px] font-mono text-[#666]">No users yet.</div>
        ) : (
          rows.map((u) => (
            <div key={u.id} data-testid={`users-row-${u.id}`} className="grid grid-cols-[1fr_1fr_100px_100px_150px_200px] gap-4 px-4 py-3 border-b border-[#111] items-center hover:bg-[#0a0a0a] transition-colors">
              <div className="text-[13px] text-white truncate">
                {u.full_name || <span className="text-[#666]">—</span>}
                {u.id === me?.id && <span className="ml-2 text-[9px] font-mono text-[#90E0EF]">(you)</span>}
              </div>
              <div className="text-[12px] font-mono text-[#c8c8c8] truncate">{u.email}</div>
              <div><RoleBadge role={u.role} /></div>
              <div><StatusPill status={u.status} /></div>
              <div className="text-[10px] font-mono text-[#888]">{fmtDate(u.last_login_at)}</div>
              <div className="flex items-center justify-end gap-1">
                {u.status !== 'active' && (
                  <button
                    type="button"
                    onClick={() => doResend(u.id)}
                    title="Resend invite"
                    data-testid={`users-resend-${u.id}`}
                    className="p-2 border border-transparent hover:border-[#333] text-[#888] hover:text-[#90E0EF] transition-colors"
                    style={{ cursor: 'pointer' }}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
                <select
                  value={u.role}
                  onChange={(e) => doPatch(u.id, { role: e.target.value })}
                  disabled={u.id === me?.id}
                  data-testid={`users-role-${u.id}`}
                  className="bg-[#0a0a0a] border border-[#222] text-[10px] font-mono uppercase tracking-[0.15em] text-[#c8c8c8] px-2 py-1 focus:outline-none focus:border-[#90E0EF] disabled:opacity-50"
                  style={{ cursor: 'pointer' }}
                >
                  <option value="admin">admin</option>
                  <option value="curator">curator</option>
                </select>
                {u.status !== 'disabled' ? (
                  <button
                    type="button"
                    onClick={() => doPatch(u.id, { status: 'disabled' })}
                    disabled={u.id === me?.id}
                    title="Disable"
                    data-testid={`users-disable-${u.id}`}
                    className="p-2 border border-transparent hover:border-[#333] text-[#888] hover:text-red-400 transition-colors disabled:opacity-30"
                    style={{ cursor: 'pointer' }}
                  >
                    <ShieldOff className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => doPatch(u.id, { status: 'active' })}
                    title="Re-enable"
                    data-testid={`users-enable-${u.id}`}
                    className="p-2 border border-transparent hover:border-[#333] text-[#888] hover:text-[#7ed4a0] transition-colors"
                    style={{ cursor: 'pointer' }}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => doDelete(u.id)}
                  disabled={u.id === me?.id}
                  title="Delete"
                  data-testid={`users-delete-${u.id}`}
                  className="p-2 border border-transparent hover:border-[#333] text-[#888] hover:text-red-400 transition-colors disabled:opacity-30"
                  style={{ cursor: 'pointer' }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AnimatePresence>
        {inviteOpen && (
          <InviteDrawer
            onClose={() => setInviteOpen(false)}
            onCreated={(res) => { setLastInvite({ user: res.user, invite_url: res.invite_url, expires_at: res.expires_at }); setInviteOpen(false); fetchRows(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function InviteDrawer({ onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('curator');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Email is required.'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/users`, {
        email: email.trim().toLowerCase(),
        full_name: fullName.trim() || null,
        role,
      });
      toast.success('Invite created.');
      onCreated(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to create invite.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-[#050505]/80 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="users-invite-drawer"
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#0a0a0a] border border-[#222] p-6"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-[#666] hover:text-white"
          style={{ cursor: 'pointer' }}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#90E0EF]">Invite user</div>
          <h2 className="mt-1 text-lg font-outfit font-semibold text-white tracking-tight">Add a new account</h2>
          <p className="mt-1 text-[11px] font-mono text-[#666]">They&apos;ll receive an email and can also use the copy-link.</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="users-invite-email"
              className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
              placeholder="new-member@example.com"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">Full name (optional)</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              data-testid="users-invite-fullname"
              className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">Role</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'admin',   caption: 'Full access to everything.' },
                { v: 'curator', caption: 'Open Call, Artists, Email, QR.' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setRole(opt.v)}
                  data-testid={`users-invite-role-${opt.v}`}
                  className={`text-left p-3 border transition-colors ${
                    role === opt.v ? 'border-[#90E0EF] bg-[#0a0f11]' : 'border-[#222] hover:border-[#333]'
                  }`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="text-[11px] font-mono uppercase tracking-[0.22em]" style={{ color: role === opt.v ? '#90E0EF' : '#f6f6f6' }}>
                    {opt.v}
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-[#666]">{opt.caption}</div>
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-red-400 text-xs font-mono" data-testid="users-invite-error">{error}</p>}

          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="users-invite-submit"
              className="flex-1 h-11 bg-[#90E0EF] text-black font-mono text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ cursor: 'pointer' }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {submitting ? 'Creating…' : 'Create invite'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 border border-[#333] text-[#888] text-[11px] font-mono uppercase tracking-[0.2em] hover:text-white hover:border-white transition-colors"
              style={{ cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
